import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Users, Trophy, Star, CheckCircle, Calendar, Volume2, X } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const HomePage = () => {
  const [isRacingMadridExpanded, setIsRacingMadridExpanded] = useState(false);

  const stats = [
    { number: "300+", label: "Young Athletes Trained" },
    { number: "15+", label: "Years Combined Experience" },
    { number: "6", label: "Specialized Programs" },
    { number: "95%", label: "Player Improvement Rate" }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 z-0">
          <video
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source
              src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/9s89ahtf_intro-video-maker-featuring-top-soccer-moments-3239g-6076-el1.mp4"
              type="video/mp4"
            />
          </video>
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-20">
          <div className="max-w-4xl">
            {/* Audio Player */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <Volume2 className="w-5 h-5 text-cyan-400" />
                <span className="text-sm text-gray-300">Football X Theme</span>
              </div>
              <audio 
                controls 
                className="w-full max-w-md h-10"
                style={{ filter: 'invert(1) grayscale(1) contrast(120%)' }}
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/0w84itt8_Football%20X%20%281%29.mp3" 
                  type="audio/mpeg" 
                />
              </audio>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              DEVELOPING
              <span className="block text-cyan-400">FUTURE STARS</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Football X is dedicated to nurturing young talent through comprehensive training programs, 
              elite coaching, and a commitment to excellence both on and off the field.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold">
                <Link to="/academy">
                  Start Your Journey <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-gray-500 text-white hover:bg-gray-800 hover:text-white">
                <Link to="/academy">Learn About Our Elite Program</Link>
              </Button>
            </div>
          </div>
        </div>
        
        <div className="absolute top-20 right-10 opacity-10 z-10">
          <div className="w-32 h-32 border-2 border-cyan-400 rounded-full"></div>
        </div>
        <div className="absolute bottom-10 right-32 opacity-10 z-10">
          <div className="w-16 h-16 bg-cyan-500 rounded-full"></div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-cyan-400 mb-2">{stat.number}</div>
                <div className="text-gray-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              PROUDLY <span className="text-cyan-400">SPONSORED BY</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              We're grateful to our valued partners who support youth development and help make our programs possible.
            </p>
          </div>
          
          <div className="flex justify-center">
            <div className="bg-gray-900 rounded-lg border-2 border-gray-800 hover:border-cyan-400 transition-all duration-300 max-w-md p-8">
              <div className="flex justify-center mb-4">
                <img 
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_4d83666a-aa6c-4973-8e91-b3c87719f098/artifacts/rh5ue7wp_IMG_2961.PNG"
                  alt="X Athlete Lab"
                  className="w-full h-auto max-h-48 object-contain"
                />
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-sm">Official Performance Partner</p>
                <p className="text-xs text-gray-500 mt-1">Strength | Speed | Conditioning | Athlete Development</p>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-gray-500 text-sm">
              Interested in becoming a sponsor? <Link to="/sponsor" className="text-cyan-400 hover:text-cyan-300 font-medium">Contact us</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              OUR <span className="text-cyan-400">PARTNERSHIPS</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              We've established strategic partnerships to provide our players with pathways to higher levels of football, 
              both locally and internationally.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {/* Leppington Lions */}
            <a href="https://llsc.com.au/" target="_blank" rel="noopener noreferrer" className="block">
              <div className="bg-gray-900 rounded-lg border-2 border-gray-800 hover:border-cyan-400 transition-all duration-300 p-6 cursor-pointer">
                <div className="text-center">
                  <img src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/ybm5r6l3_leppington-lions-high-res-logo-nobkgrnd.png" alt="Leppington Lions Soccer Club" className="w-full h-auto max-h-20 object-contain mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Leppington Lions Soccer Club</h3>
                  <p className="text-gray-400 text-sm">Local Football Partnership</p>
                </div>
              </div>
            </a>

            {/* Sunshine Coast */}
            <div className="bg-gray-900 rounded-lg border-2 border-gray-800 hover:border-cyan-400 transition-all duration-300 p-6">
              <div className="text-center">
                <img src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/wa53pb2w_sunshine%20Coast.png" alt="Sunshine Coast Fire UK" className="w-full h-auto max-h-20 object-contain mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Sunshine Coast Fire UK</h3>
                <p className="text-gray-400 text-sm mb-2">UK Pathway Partnership</p>
                <p className="text-xs text-gray-500">Plays out of Bishop's Castle - Providing Young Australians a UK Pathway</p>
              </div>
            </div>

            {/* CRS Sportswear */}
            <a href="https://www.facebook.com/p/CRS-Sportswear-61576620682286/" target="_blank" rel="noopener noreferrer" className="block">
              <div className="bg-gray-900 rounded-lg border-2 border-gray-800 hover:border-cyan-400 transition-all duration-300 p-6 cursor-pointer">
                <div className="text-center">
                  <img src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/5g6v0sdz_CRS.jpg" alt="CRS Sportswear" className="w-full h-auto max-h-20 object-contain mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">CRS Sportswear</h3>
                  <p className="text-gray-400 text-sm mb-2">Official Apparel Partner</p>
                  <p className="text-xs text-gray-500">Number one manufacturer of the highest quality custom designed sports wear</p>
                </div>
              </div>
            </a>

            {/* Sports Recruiting USA */}
            <a href="https://www.sportsrecruitingusa.com/" target="_blank" rel="noopener noreferrer" className="block">
              <div className="bg-gray-900 rounded-lg border-2 border-gray-800 hover:border-cyan-400 transition-all duration-300 p-6 cursor-pointer">
                <div className="text-center">
                  <img src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/f6pvdosm_SRUSA.png" alt="Sports Recruiting USA" className="w-full h-auto max-h-20 object-contain mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Sports Recruiting USA</h3>
                  <p className="text-gray-400 text-sm mb-2">USA College Pathway</p>
                  <p className="text-xs text-gray-500">World's Leading U.S College Recruiting Agency</p>
                </div>
              </div>
            </a>

            {/* Penrith FC */}
            <div className="bg-gray-900 rounded-lg border-2 border-gray-800 hover:border-cyan-400 transition-all duration-300 p-6">
              <div className="text-center">
                <img src="https://customer-assets-lqy194kg.emergentagent.net/job_4d83666a-aa6c-4973-8e91-b3c87719f098/artifacts/udl4jhm1_Penrith-FC-300x264.png" alt="Penrith FC" className="w-full h-auto max-h-20 object-contain mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Penrith FC</h3>
                <p className="text-gray-400 text-sm">Local Football Partnership</p>
              </div>
            </div>
          </div>

          {/* Racing Madrid Modal */}
          {isRacingMadridExpanded && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
              <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-8">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-center space-x-4">
                      <img src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/wfct85uf_racing%20madrid.png" alt="Racing Madrid 1914 FC" className="w-16 h-auto object-contain" />
                      <div>
                        <h3 className="text-3xl font-bold mb-2">Racing Madrid 1914 FC</h3>
                        <p className="text-cyan-400 font-semibold text-lg">YOUR PATH TO PROFESSIONAL FOOTBALL</p>
                      </div>
                    </div>
                    <button onClick={() => setIsRacingMadridExpanded(false)} className="text-gray-400 hover:text-white transition-colors">
                      <X className="w-8 h-8" />
                    </button>
                  </div>
                  
                  <p className="text-gray-300 mb-8 text-lg">
                    Racing Madrid 1914 FC invites elite youth players (ages 10–23) to immerse in European football's epicenter. 
                    Train under UEFA PRO-certified coaches with proven records at top-tier clubs.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-4">Elite Development Program</h4>
                      <ul className="space-y-3 text-gray-300">
                        <li className="flex items-start space-x-3">
                          <span className="text-cyan-400 font-bold text-lg">•</span>
                          <span><strong>Technical Mastery:</strong> Develop under Spain's highest tactical standards</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <span className="text-cyan-400 font-bold text-lg">•</span>
                          <span><strong>24/7 Pro Environment:</strong> Live, train, and compete like a La Liga academy prospect</span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <span className="text-cyan-400 font-bold text-lg">•</span>
                          <span><strong>Holistic Growth:</strong> Physical conditioning, technical drills, and performance analysis</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold mb-4">Program Details</h4>
                      <ul className="space-y-3 text-gray-300">
                        <li><strong>Duration:</strong> January–March 2026</li>
                        <li><strong>Location:</strong> Madrid, Spain</li>
                        <li><strong>Ages:</strong> 10-23 years (male/female)</li>
                        <li><strong>Eligibility:</strong> Open to international players seeking elite development</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="pt-6 border-t border-gray-700">
                    <h4 className="text-xl font-semibold mb-6 text-center">Expert Coaching Staff</h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-gray-800 p-6 rounded-lg">
                        <h5 className="font-bold text-lg mb-2">Head Coach: Ruben De La Red</h5>
                        <p className="text-gray-300">Product of Real Madrid's academy, European Champion with Spain in 2008. Experience at Real Madrid Youth, Getafe B, Guangzhou FC Youth, and Al-Nasr SC.</p>
                      </div>
                      <div className="bg-gray-800 p-6 rounded-lg">
                        <h5 className="font-bold text-lg mb-2">Assistant Coach: Ricardo Lopez Felipe</h5>
                        <p className="text-gray-300">International experience including Japan national team goalkeeping coach. Playing career at Real Valladolid, Racing Santander, Manchester United, and Osasuna.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center mt-8">
                    <Button onClick={() => setIsRacingMadridExpanded(false)} className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold px-8 py-3">
                      Close Details
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 2027 EOI Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              FOOTBALL X ACADEMY <span className="text-cyan-400">2027</span>
            </h2>
            <h3 className="text-2xl md:text-3xl font-bold mb-2">
              FULL TIME & <span className="text-cyan-400">PART TIME</span> PROGRAMS
            </h3>
            <p className="text-lg text-gray-400 mt-4 italic">
              Develop. Compete. <span className="text-cyan-400 font-semibold">Excel.</span>
            </p>
            <div className="mt-4 inline-block bg-cyan-500 text-black font-bold px-6 py-2 rounded-full text-lg">
              AGES: U7's TO U13's
            </div>
          </div>

          <div className="flex justify-center mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_4d83666a-aa6c-4973-8e91-b3c87719f098/artifacts/zqkw8qrg_sports-logo-generator-for-a-soccer-team-league-5384a-el1%20%286%29.webp"
              alt="Football X Academy"
              className="h-40 w-auto drop-shadow-[0_0_25px_rgba(34,211,238,0.3)]"
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 max-w-5xl mx-auto">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-cyan-400 transition-colors">
              <Users className="w-10 h-10 text-cyan-400 mb-4" />
              <h4 className="text-lg font-bold mb-2">Elite Player Development</h4>
              <p className="text-gray-400 text-sm">High quality training focused on skill, confidence and long term player development.</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-cyan-400 transition-colors">
              <Trophy className="w-10 h-10 text-cyan-400 mb-4" />
              <h4 className="text-lg font-bold mb-2">Professional Coaching</h4>
              <p className="text-gray-400 text-sm">Experienced, qualified coaches committed to bringing out the best in every player.</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-cyan-400 transition-colors">
              <Target className="w-10 h-10 text-cyan-400 mb-4" />
              <h4 className="text-lg font-bold mb-2">Competitive Pathway</h4>
              <p className="text-gray-400 text-sm">Clear pathway with opportunities to grow, compete and reach your full potential.</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-cyan-400 transition-colors">
              <Star className="w-10 h-10 text-cyan-400 mb-4" />
              <h4 className="text-lg font-bold mb-2">Character & Leadership</h4>
              <p className="text-gray-400 text-sm">Building strong individuals on and off the field through values and teamwork.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
            <div className="bg-gray-900 border border-cyan-400/30 rounded-xl p-6 text-center">
              <Calendar className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
              <h4 className="text-xl font-bold mb-1">2 TO 3 NIGHTS</h4>
              <p className="text-cyan-400 font-semibold text-sm mb-2">A WEEK TRAINING</p>
              <p className="text-gray-400 text-sm">Consistent training. Real development. Better players.</p>
            </div>
            <div className="bg-gray-900 border border-cyan-400/30 rounded-xl p-6 text-center">
              <Trophy className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
              <h4 className="text-xl font-bold mb-1">COMPETITION</h4>
              <p className="text-cyan-400 font-semibold text-sm mb-2">SPL 1, 2 AND 3</p>
              <p className="text-gray-400 text-sm">Compete at the highest level in the SPL league system.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto mb-12">
            {[
              { icon: <Users className="w-6 h-6" />, title: "Full Time Program", desc: "Comprehensive training, education and player development." },
              { icon: <Calendar className="w-6 h-6" />, title: "Part Time Program", desc: "Flexible schedule with high quality training." },
              { icon: <Users className="w-6 h-6" />, title: "Ages U7's to U13's", desc: "Age appropriate training for every stage." },
              { icon: <Trophy className="w-6 h-6" />, title: "Competition SPL", desc: "Compete at the highest level in SPL." },
              { icon: <CheckCircle className="w-6 h-6" />, title: "More Than A Team", desc: "It's a community. It's a culture." },
            ].map((item, i) => (
              <div key={i} className="text-center p-3">
                <div className="text-cyan-400 flex justify-center mb-2">{item.icon}</div>
                <h5 className="text-sm font-bold mb-1">{item.title}</h5>
                <p className="text-gray-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-cyan-500 rounded-2xl p-8 text-center max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold text-black mb-2">2027 EXPRESSION OF INTEREST</h3>
            <p className="text-xl font-bold text-black mb-1">NOW OPEN</p>
            <p className="text-black/70 mb-6">Spaces are limited — register today</p>
            <Button asChild size="lg" className="bg-black hover:bg-gray-800 text-white font-semibold px-8">
              <a href="https://form.jotform.com/261577826480063" target="_blank" rel="noopener noreferrer">Register Your Interest <ArrowRight className="ml-2 w-5 h-5" /></a>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              WHY CHOOSE <span className="text-cyan-400">FOOTBALL X</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Elite Coaching</h3>
              <p className="text-gray-400">AFC qualified coaches with proven track records in player development.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Focused Development</h3>
              <p className="text-gray-400">Comprehensive programs targeting technical, physical, and mental aspects.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Community Focus</h3>
              <p className="text-gray-400">Building lasting relationships and fostering teamwork within our football family.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Proven Results</h3>
              <p className="text-gray-400">Track record of developing players who excel at higher levels of competition.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-cyan-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            READY TO START YOUR FOOTBALL JOURNEY?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-2xl mx-auto">
            Join hundreds of young athletes who have chosen Football X to develop their skills and reach their potential.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-black hover:bg-gray-800 text-white">
              <Link to="/academy">Register for Academy</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-black text-black hover:bg-black hover:text-white">
              <Link to="/academy">Apply for Elite Program</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
